import React from 'react';
import { AlertContext, AlertContextType, PatientService, ServicesContext } from '~/services';
import { ProviderPrint, ReferralAttachmentType } from '~/components/Patient';
import { ReferralModal } from '~/components/Patient/Referrals/ReferralInfo/ReferralAction/ReferralModal';
import { Spinner } from 'react-bootstrap';
import { ReferralType, enumValue } from '~/util';
import { ProviderEntityEnum, ProviderType, getProviderInfo } from '~/util/providerUtils';
import { ReferralContext, ReferralContextType } from '~/services/ReferralContext';
import { ConfirmReferralModal } from './ConfirmReferralModal';

interface UploadedAttachment {
    // eslint-disable-next-line babel/camelcase
    display_order: number;
    // eslint-disable-next-line babel/camelcase
    media_id: number;
    objective: number;
}
export interface ReferralActionProps {
    patient: PatientType;
    provider: ProviderType;
    planId: number | undefined;
    reason: string;
    notes: string;
    email: string;
    attachments: ReferralAttachmentType[];
    sendingReferral: boolean;
    setSendingReferral: (x: boolean) => void;
    selectedLocation?: undefined | LocationType;
    setMissingRequiredLocation: (x: boolean) => void;
    suggestedEdit: boolean;
}

export function ReferralAction({
    patient,
    provider,
    planId,
    reason,
    notes,
    email,
    attachments,
    sendingReferral,
    setSendingReferral,
    selectedLocation,
    setMissingRequiredLocation,
    suggestedEdit,
}: ReferralActionProps) {
    const context = React.useContext(ServicesContext);
    const alertContext = React.useContext(AlertContext) as AlertContextType;
    const { clearSelectedProvider } = React.useContext(ReferralContext) as ReferralContextType;
    const [referralSuccess, setReferralSuccess] = React.useState(false);
    const [referralError, setReferralError] = React.useState('');
    const [patientDuplicates, setPatientDuplicates] = React.useState([]);
    const [selectedPatient, setSelectedPatient] = React.useState('');
    const [sentReferral, setSentReferral] = React.useState<ReferralType>();
    const [showConfirmModal, setShowConfirmModal] = React.useState(false);
    const uploadedAttachments = React.useRef<UploadedAttachment[]>([]);
    const { providerFax, providerIsContact } = getProviderInfo(provider);

    async function uploadAttachments() {
        if (attachments.length > 0) {
            const data = new FormData();
            data.append('form[enabled]', '1');
            data.append('form[owning_entity]', 'organization');
            attachments.forEach((attachment, i) => {
                data.append(`form[name][${i}]`, attachment.file.name);
                data.append(`form[description][${i}]`, '');
                data.append(`form[is_public][${i}]`, '0');
                data.append(`form[binary_content][${i}]`, attachment.file);
                data.append(`form[has_custom_thumb][${i}]`, '0');
                data.append(`form[has_organization_name][${i}]`, '0');
                data.append(`form[add_to_library][${i}]`, 'false');
                data.append(`form[skip_mime_validation][${i}]`, 'true');
            });
            const mediaResponse = await context.api.call(
                {
                    method: 'post',
                    url: '/core-api/media',
                    data,
                },
                { handledErrors: true }
            );
            uploadedAttachments.current = mediaResponse.data.media.map(
                (item: { id: number }, i: number) => ({
                    display_order: i + 1,
                    media_id: item.id,
                    objective: 1,
                })
            );
        } else {
            uploadedAttachments.current = [];
        }
    }

    async function sendReferral(type: string, confirmSend = false) {
        if (suggestedEdit && providerIsContact && !confirmSend) {
            setShowConfirmModal(true);
            return;
        }
        if (!selectedLocation && provider.locations && provider.locations.length > 0) {
            setMissingRequiredLocation(true);
            return;
        }
        setMissingRequiredLocation(false);
        setShowConfirmModal(false);
        setSendingReferral(true);
        try {
            let orgId = null;
            if (provider.entityType === ProviderEntityEnum.ORG) orgId = provider.id;
            if (provider.entityType === ProviderEntityEnum.PHYSICIAN) orgId = provider.orgId;
            // don't upload attachments twice when resolving duplicate patient
            if (uploadedAttachments.current.length === 0) {
                await uploadAttachments();
            }
            const referralResponse = await context.api.call(
                {
                    method: 'post',
                    url: '/core-api/referrals',
                    data: {
                        patient_id: patient.id,
                        patient_email: email || null,
                        reason,
                        notes,
                        plan_id: planId,
                        plan_owning_org_id: provider.planOwningOrg,
                        organization_id: orgId,
                        physician_id:
                            provider.entityType === ProviderEntityEnum.PHYSICIAN
                                ? provider.id
                                : null,
                        contact_id: providerIsContact ? provider.id : null,
                        contact_npi: providerIsContact ? provider.npi : null,
                        contact_number: providerFax?.number,
                        core_app_patient_id: type === 'select' ? selectedPatient : null,
                        force_create_patient: type === 'force' ? true : null,
                        message_media: uploadedAttachments.current,
                        // -1 will translate to Any Location while null would
                        // translate to the relevant org's default location
                        location_id: selectedLocation?.id || -1,
                    },
                },
                { handledErrors: true }
            );
            context.events.publish(PatientService.EVENTS.notes_updated);
            if (referralResponse?.data?.message) {
                setSentReferral(referralResponse.data.message);
            }
            setPatientDuplicates([]);
            setReferralSuccess(true);
        } catch (e) {
            if (e.response.status === 412 && Array.isArray(e.response.data?.patients)) {
                setPatientDuplicates(e.response.data.patients);
            } else if (
                e.response.status === 422 &&
                e.response.data?.message?.['email.address.invalid']
            ) {
                setReferralError('invalid_email');
            } else if (
                e.response.status === 422 &&
                e.response.data?.message?.['null.null.error'] ===
                    'The uploaded file was too large. Please try to upload a smaller file.'
            ) {
                setReferralError('large_file');
            } else if (
                e.response.status === 422 &&
                e.response.data?.message?.['invitation.from_organization.duplicate']
            ) {
                setReferralError('connection');
            } else if (
                e.response.status === 422 &&
                e.response.data?.message?.['error.description'] ===
                    'Token does not have the required roles.'
            ) {
                setReferralError('missing_roles');
            } else if (
                e.response.status === 400 &&
                e.response.data?.message === 'User account is disabled.'
            ) {
                setReferralError('disabled_user');
            } else {
                setReferralError('generic');
            }
        } finally {
            setSendingReferral(false);
        }
    }

    function closeConfirm() {
        setReferralSuccess(false);
        setSelectedPatient('');
        setSentReferral(undefined);
        uploadedAttachments.current = [];
        if (referralError) {
            setReferralError('');
        } else if (patientDuplicates.length > 0) {
            setPatientDuplicates([]);
        } else {
            alertContext.activateTriggeredBanner(
                enumValue(['banners', 'triggers', 'Send Referral'])
            );
            clearSelectedProvider();
        }
    }

    const isDisabled = reason.trim().length === 0;

    return (
        <>
            <ConfirmReferralModal
                show={showConfirmModal}
                close={() => setShowConfirmModal(false)}
                sendReferral={sendReferral}
                patient={patient}
                provider={provider}
                reason={reason}
                notes={notes}
                patientEmail={email}
                location={selectedLocation}
            />
            <ReferralModal
                close={closeConfirm}
                referralError={referralError}
                referralSuccess={referralSuccess}
                patient={patient}
                provider={provider}
                reason={reason}
                notes={notes}
                selectedPatient={selectedPatient}
                setSelectedPatient={setSelectedPatient}
                sendReferral={sendReferral}
                loading={sendingReferral}
                patientDuplicates={patientDuplicates}
                sentReferral={sentReferral}
                patientEmail={email}
                location={selectedLocation}
            />
            {context.user.hasFeatureToggle('send_referral') &&
            context.user.getExternalAccount('Core App') &&
            (!providerIsContact || providerFax) ? (
                <button
                    type="button"
                    className="btn btn-primary"
                    disabled={isDisabled || sendingReferral}
                    onClick={() => sendReferral('')}
                >
                    {sendingReferral && (
                        <Spinner
                            animation="border"
                            variant="light"
                            size="sm"
                            role="status"
                            className="me-1"
                        />
                    )}
                    Send Referral
                </button>
            ) : (
                <ProviderPrint
                    referralStatus="unconfirmed"
                    patient={patient}
                    provider={provider}
                    reason={reason}
                    notes={notes}
                    isDisabled={isDisabled}
                    patientEmail={email}
                    location={selectedLocation}
                />
            )}
        </>
    );
}
